/**
 * Chain Panel — Side navigation panel for clarification chains (desktop only).
 *
 * Features:
 *   - Auto-shows when chain depth >= 2 on desktop (>991px)
 *   - User can dismiss (remembered per session; new chain root re-triggers)
 *   - Resizable via drag handle on right edge; width persisted in cookie
 *   - Safe min/max limits based on viewport width
 *   - Collapses active branch in dropdown when open
 *   - Full branch tree rendering via shared _renderBranch
 *
 * Exports: window.CHAIN_PANEL.open/close/toggle/update/isOpen
 */

(function () {
  'use strict';

  // Quote-safe escaper (BL-050): the shared history helper, never the identity.
  function _chainEsc() {
    if (typeof window._safeEscape === 'function') return window._safeEscape;
    return function (s) {
      return String(s === null || s === undefined ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    };
  }

  var SESSION_KEY = 'slovary-chain-panel-dismissed';
  var SESSION_ROOT_KEY = 'slovary-chain-panel-last-root';
  var WIDTH_COOKIE = 'slovary_chain_panel_width';
  var DESKTOP_MIN = 992;
  var DEFAULT_WIDTH = 320;
  var MIN_WIDTH = 220;
  var MAIN_MAX_WIDTH = 900; // #all max-width
  var FAB_SPACE = 40; // FAB button (32px) + gap (8px)
  var GAP = 24; // extra breathing room between FAB and #all

  var _isOpen = false;
  var _currentChain = [];
  var _currentRootId = null;
  var _panelWidth = DEFAULT_WIDTH;
  var _isResizing = false;
  // id ряда с pending-save; нужен, чтобы при open/close панели перерисовывать
  // индикатор в правильном режиме (inline в панели vs плавающий fab).
  var _pendingSaveId = null;

  // Tail per root: самое глубокое посещённое слово в цепочке.
  // Не сбрасывается при подъёме через breadcrumb — позволяет показывать
  // "child" справа от current, пока current является предком tail'а.
  var _chainTailPerRoot = {};

  // ---- DOM refs ----
  function _panel() { return document.getElementById('chain-panel'); }
  function _fab() { return document.getElementById('chain-panel-fab'); }
  function _body() { return document.getElementById('chain-panel-body'); }
  function _resizeHandle() { return document.getElementById('chain-panel-resize'); }

  // ---- Helpers ----
  function _isDesktop() { return window.innerWidth >= DESKTOP_MIN; }

  function _getMaxWidth() {
    // Panel must not overlap #all. When panel is open, body gets padding-left,
    // and #all (max-width 900px, margin:auto) centers in remaining space.
    // Remaining space = viewport - panelWidth. #all needs at least MAIN_MAX_WIDTH
    // or full remaining width on smaller screens, plus a gap.
    // So: panelWidth <= viewport - MAIN_MAX_WIDTH - GAP (on wide screens)
    // Fallback: never more than 50% of viewport.
    var vw = window.innerWidth;
    var maxFromContent = Math.floor(vw - MAIN_MAX_WIDTH - FAB_SPACE - GAP);
    var maxFromRatio = Math.floor(vw * 0.5);
    return Math.max(MIN_WIDTH, Math.min(maxFromContent, maxFromRatio));
  }

  function _clampWidth(w) {
    return Math.max(MIN_WIDTH, Math.min(w, _getMaxWidth()));
  }

  function _isDismissedForSession() {
    try { return sessionStorage.getItem(SESSION_KEY) === '1'; }
    catch (e) { return false; }
  }
  function _setDismissedForSession(val) {
    try {
      if (val) sessionStorage.setItem(SESSION_KEY, '1');
      else sessionStorage.removeItem(SESSION_KEY);
    } catch (e) {}
  }
  function _getLastAutoRoot() {
    try { return sessionStorage.getItem(SESSION_ROOT_KEY) || null; }
    catch (e) { return null; }
  }
  function _setLastAutoRoot(rootId) {
    try {
      if (rootId) sessionStorage.setItem(SESSION_ROOT_KEY, rootId);
      else sessionStorage.removeItem(SESSION_ROOT_KEY);
    } catch (e) {}
  }

  // ---- Width cookie ----
  function _loadWidthFromCookie() {
    try {
      var match = document.cookie.match('(?:^|; )' + WIDTH_COOKIE + '=(\\d+)');
      if (match) {
        var w = parseInt(match[1], 10);
        if (w >= MIN_WIDTH) return _clampWidth(w);
      }
    } catch (e) {}
    return DEFAULT_WIDTH;
  }

  function _saveWidthToCookie(w) {
    try {
      var d = new Date();
      d.setFullYear(d.getFullYear() + 1);
      document.cookie = WIDTH_COOKIE + '=' + Math.round(w)
        + '; path=/; expires=' + d.toUTCString() + '; SameSite=Lax';
    } catch (e) {}
  }

  function _applyWidth(w) {
    _panelWidth = _clampWidth(w);
    document.documentElement.style.setProperty('--chain-panel-width', _panelWidth + 'px');
  }

  // ---- Lang ----
  function _getLangTexts() {
    var lang = 'en';
    if (window.SEARCH_LANGUAGE && window.SEARCH_LANGUAGE.getCurrentLang) {
      lang = window.SEARCH_LANGUAGE.getCurrentLang();
    }
    var texts = (window.LANG_TEXTS && window.LANG_TEXTS[lang]) || {};
    return {
      empty: texts.chain_panel_empty || 'Click a cross-link to start a chain'
    };
  }

  // ---- Rendering (reuses _buildHistoryTree + _renderBranch) ----

  function _renderPanelContent() {
    var historyId = window.CURRENT_HISTORY_ID;
    if (!historyId) {
      var t = _getLangTexts();
      var esc = _chainEsc()(t.empty);
      return '<div class="chain-panel-empty">' + esc + '</div>';
    }

    var history = [];
    if (typeof getHistory === 'function') history = getHistory();
    else if (window.getHistory) history = window.getHistory();
    if (history.length === 0) return '';

    var tree = (typeof window._buildHistoryTree === 'function') ? window._buildHistoryTree(history) : null;
    if (!tree) return '';

    var activeChain = (typeof window._computeActiveChain === 'function')
      ? window._computeActiveChain(historyId, tree.nodes) : {};

    // Find root of current item's branch
    var byId = {};
    history.forEach(function (it) { byId[it.id] = it; });
    var cur = byId[historyId];
    var activeRootId = null;
    var guard = 0;
    while (cur && guard < 100) {
      if (!cur.parentId || !byId[cur.parentId]) { activeRootId = cur.id; break; }
      cur = byId[cur.parentId];
      guard++;
    }

    var rootNode = null;
    for (var i = 0; i < tree.roots.length; i++) {
      if (tree.roots[i].item.id === activeRootId) { rootNode = tree.roots[i]; break; }
    }

    if (!rootNode || typeof window._renderBranch !== 'function') return '';
    return window._renderBranch(rootNode, activeChain);
  }

  function _updatePanelContent() {
    var body = _body();
    if (!body) return;
    body.innerHTML = _renderPanelContent();
    var current = body.querySelector('.search-history-item.is-active');
    if (current) current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    // Wake from idle blur — content changed, user should see the update
    _wakeFromIdle();
  }

  /**
   * Returns true if the root's subtree has any node with >= 2 direct children
   * (цепочка разветвляется). Auto-open боковой панели завязан на это:
   * линейная цепочка → достаточно breadcrumb, панель не открываем автоматом.
   */
  function _treeHasBranching(rootId) {
    return _subtreeHasBranchingFrom(rootId);
  }

  /**
   * Returns true if the subtree rooted at `startId` (включительно) содержит
   * узел с >= 2 прямыми потомками. То есть СМОТРИМ ВПЕРЁД от startId и до конца
   * цепочки — есть ли разветвление. Если startId — leaf, вернёт false.
   */
  function _subtreeHasBranchingFrom(startId) {
    if (!startId || !window.getHistory) return false;
    var history = window.getHistory();
    // Собираем ids поддерева (startId + все его потомки).
    var subtree = {};
    subtree[startId] = true;
    var changed = true;
    var guard = 0;
    while (changed && guard < 1000) {
      changed = false;
      for (var i = 0; i < history.length; i++) {
        var it = history[i];
        if (it.parentId && subtree[it.parentId] && !subtree[it.id]) {
          subtree[it.id] = true;
          changed = true;
        }
      }
      guard++;
    }
    // Для каждого узла поддерева считаем прямых детей.
    var counts = {};
    for (var j = 0; j < history.length; j++) {
      var p = history[j].parentId;
      if (p && subtree[p]) {
        counts[p] = (counts[p] || 0) + 1;
        if (counts[p] >= 2) return true;
      }
    }
    return false;
  }

  /**
   * Returns true if the current item's branch has depth >= 2
   * (root + at least one child), regardless of where in the branch
   * the current item is.
   */
  function _branchHasChain() {
    if (!_currentRootId) return _currentChain.length >= 2;
    // Check if root has any children
    var children = [];
    if (typeof getChildren === 'function') {
      children = getChildren(_currentRootId);
    } else if (window.getHistoryChildren) {
      children = window.getHistoryChildren(_currentRootId);
    }
    return children.length > 0;
  }

  function _updateFabVisibility() {
    var fab = _fab();
    if (!fab) return;
    var hasChain = _branchHasChain();
    if (hasChain && _isDesktop()) {
      fab.classList.add('is-visible');
      fab.classList.toggle('is-panel-open', _isOpen);
    } else {
      fab.classList.remove('is-visible', 'is-panel-open');
    }
  }

  // ---- Dropdown sync ----
  function _collapseDropdownChainBranch() {
    if (!_currentRootId) return;
    var hl = document.getElementById('search-history-dropdown-list');
    if (hl && typeof _setBranchCollapsed === 'function') _setBranchCollapsed(hl, _currentRootId, true);
  }
  function _expandDropdownChainBranch() {
    if (!_currentRootId) return;
    var hl = document.getElementById('search-history-dropdown-list');
    if (hl && typeof _setBranchCollapsed === 'function') _setBranchCollapsed(hl, _currentRootId, false);
  }

  // ---- Open / Close ----

  function open() {
    if (_isOpen || !_isDesktop()) return;
    var panel = _panel();
    if (!panel) return;

    _isOpen = true;
    panel.classList.add('is-open');
    panel.classList.remove('is-idle');
    document.body.classList.add('chain-panel-active');
    _updateFabVisibility();
    _updatePanelContent();
    _setDismissedForSession(false);
    _startIdleTimer();
    _collapseDropdownChainBranch();
    // Перенести индикатор pending-save из плавающего fab внутрь панели.
    if (_pendingSaveId) showPendingSave(_pendingSaveId);
  }

  function close() {
    if (!_isOpen) return;
    var panel = _panel();
    if (panel) {
      panel.classList.remove('is-open');
      panel.classList.remove('is-idle');
    }

    _isOpen = false;
    _clearIdleTimer();
    document.body.classList.remove('chain-panel-active');
    _updateFabVisibility();
    _setDismissedForSession(true);
    _expandDropdownChainBranch();
    // Перенести индикатор pending-save в плавающий fab (правый верхний угол).
    if (_pendingSaveId) showPendingSave(_pendingSaveId);
  }

  function toggle() { _isOpen ? close() : open(); }
  function isOpen() { return _isOpen; }

  // ---- Resize drag ----

  function _startResize(e) {
    e.preventDefault();
    _isResizing = true;
    var panel = _panel();
    if (panel) panel.classList.add('is-resizing');
    document.body.classList.add('chain-panel-resizing');

    var handle = _resizeHandle();
    if (handle) handle.classList.add('is-dragging');

    document.addEventListener('mousemove', _onResizeMove);
    document.addEventListener('mouseup', _stopResize);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }

  function _onResizeMove(e) {
    if (!_isResizing) return;
    var newWidth = _clampWidth(e.clientX);
    _applyWidth(newWidth);
  }

  function _stopResize() {
    if (!_isResizing) return;
    _isResizing = false;
    var panel = _panel();
    if (panel) panel.classList.remove('is-resizing');
    document.body.classList.remove('chain-panel-resizing');

    var handle = _resizeHandle();
    if (handle) handle.classList.remove('is-dragging');

    document.removeEventListener('mousemove', _onResizeMove);
    document.removeEventListener('mouseup', _stopResize);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    _saveWidthToCookie(_panelWidth);
  }

  // ---- Breadcrumb + history-button chain-mode helpers ----

  /**
   * Возвращает количество узлов во всём дереве корня (root + все потомки).
   * Используется для бейджа на history-кнопке.
   */
  function _countChainTree(rootId) {
    if (!rootId || !window.getHistory) return 0;
    var history = window.getHistory();
    var count = 0;
    function collect(pid) {
      for (var i = 0; i < history.length; i++) {
        if (history[i].parentId === pid) {
          count++;
          collect(history[i].id);
        }
      }
    }
    // Root сам тоже считается.
    for (var j = 0; j < history.length; j++) {
      if (history[j].id === rootId) { count++; break; }
    }
    collect(rootId);
    return count;
  }

  function _updateHistoryButton(inChain, treeCount) {
    var btn = document.getElementById('search-history-button');
    if (!btn) return;
    var badge = document.getElementById('chain-mode-badge');
    btn.classList.toggle('chain-mode-active', !!inChain);
    if (badge) {
      if (inChain && treeCount > 0) {
        badge.textContent = String(treeCount);
        badge.hidden = false;
      } else {
        badge.textContent = '';
        badge.hidden = true;
      }
    }
  }

  /**
   * Resolve tail для текущей цепочки.
   *   - Если сохранённый tail существует и current является его предком → tail не меняем
   *     (пользователь поднялся по цепочке через breadcrumb, tail остаётся на дне).
   *   - Иначе tail = currentId (новая позиция — либо ушли вглубь, либо в другую ветку).
   */
  function _resolveTail(rootId, currentId) {
    if (!rootId || !currentId) return null;
    var storedTail = _chainTailPerRoot[rootId];
    if (storedTail && window.getHistoryRootChain) {
      var tailChain = window.getHistoryRootChain(storedTail);
      var currentInTailChain = tailChain.some(function(it) { return it.id === currentId; });
      if (currentInTailChain) {
        return storedTail;
      }
    }
    _chainTailPerRoot[rootId] = currentId;
    return currentId;
  }

  function _renderBreadcrumb(chain, currentId) {
    if (!chain || chain.length === 0) return '';

    var esc = _chainEsc();
    var arrow = '<span class="chain-breadcrumb-sep" aria-hidden="true"><i class="bi bi-chevron-right"></i></span>';

    var rootId = chain[0].id;
    var children = window.getHistoryChildren ? window.getHistoryChildren(currentId) : [];

    // Скрываем только если нет ни предков, ни потомков (одинокий root — не цепочка).
    if (chain.length < 2 && children.length === 0) return '';

    // Ancestors: root + (ellipsis if gap) + parent. Current в КОНЦЕ левой части.
    // chain.length === 1: current IS root, предков нет
    // chain.length === 2: [root, current] → root › current
    // chain.length === 3: [root, parent, current] → root › parent › current
    // chain.length >= 4: [root, ..., parent, current] → root › … › parent › current
    var items = [];
    if (chain.length === 2) {
      items.push({ node: chain[0], kind: 'root' });
    } else if (chain.length === 3) {
      items.push({ node: chain[0], kind: 'root' });
      items.push({ node: chain[1], kind: 'mid' });
    } else if (chain.length >= 4) {
      items.push({ node: chain[0], kind: 'root' });
      items.push({ kind: 'ellipsis' });
      items.push({ node: chain[chain.length - 2], kind: 'mid' });
    }
    items.push({ node: chain[chain.length - 1], kind: 'current' });

    // Right-side: прямой потомок current + hint о сиблингах, если их несколько.
    if (children.length > 0) {
      // Предпочтение: tail-path (куда пользователь ходил раньше), иначе самый свежий по timestamp.
      var preferred = null;
      var tailId = _resolveTail(rootId, currentId);
      if (tailId && tailId !== currentId && window.getHistoryRootChain) {
        var tailChain = window.getHistoryRootChain(tailId);
        for (var ti = 0; ti < tailChain.length; ti++) {
          if (tailChain[ti].id === currentId && ti + 1 < tailChain.length) {
            preferred = tailChain[ti + 1];
            break;
          }
        }
      }
      if (!preferred) {
        preferred = children.slice().sort(function(a, b) {
          return (b.timestamp || 0) - (a.timestamp || 0);
        })[0];
      }
      items.push({ node: preferred, kind: 'child' });
      // Цепочка продолжается ниже показанного потомка → «› …», иначе глубокая
      // (например, 10-узловая) цепочка выглядит так, будто она кончается прямо
      // на этом потомке (баг-репорт 2026-07-19).
      var grandChildren = window.getHistoryChildren ? window.getHistoryChildren(preferred.id) : [];
      if (grandChildren.length > 0) {
        items.push({ kind: 'ellipsis' });
      }
      if (children.length > 1) {
        items.push({ kind: 'siblings', count: children.length - 1 });
      }
    } else {
      // У current нет потомков — ничего не добавляем справа (конец цепочки
      // виден по отсутствию продолжения).
      _resolveTail(rootId, currentId);
    }

    var parts = ['<i class="bi bi-diagram-3-fill chain-breadcrumb-icon" aria-hidden="true"></i>'];
    parts.push('<div class="chain-breadcrumb-items">');
    items.forEach(function(it, i) {
      // Разделитель не нужен перед служебными маркерами (leaf, siblings) —
      // они не самостоятельные узлы, а хинты к предыдущему элементу.
      if (i > 0 && it.kind !== 'leaf' && it.kind !== 'siblings') {
        parts.push(arrow);
      }
      if (it.kind === 'ellipsis') {
        parts.push('<span class="chain-breadcrumb-ellipsis" aria-hidden="true">…</span>');
        return;
      }
      if (it.kind === 'siblings') {
        parts.push(
          '<button type="button" class="chain-breadcrumb-siblings" title="+' + it.count + '" aria-haspopup="menu" aria-expanded="false">' +
            '+' + it.count +
          '</button>'
        );
        return;
      }
      var label = esc(it.node.displayTerm || it.node.term || it.node.query || '');
      if (it.kind === 'current') {
        parts.push(
          '<span class="chain-breadcrumb-item is-current" title="' + label + '" aria-current="page">' +
            label +
          '</span>'
        );
      } else {
        parts.push(
          '<button type="button" class="chain-breadcrumb-item" data-chain-id="' + esc(it.node.id) + '" title="' + label + '">' +
            label +
          '</button>'
        );
      }
    });
    parts.push('</div>');
    return parts.join('');
  }

  function _updateBreadcrumb(chain, currentId) {
    var el = document.getElementById('chain-breadcrumb');
    if (!el) return;
    var html = _renderBreadcrumb(chain || [], currentId);
    if (!html) {
      el.innerHTML = '';
      el.hidden = true;
      return;
    }
    el.innerHTML = html;
    el.hidden = false;
    _syncSearchFormStickyHeight();
  }

  /**
   * Обновляет CSS-переменную --search-form-sticky-height, чтобы sticky-breadcrumb
   * прилипал ровно под формой поиска без перекрытия.
   */
  function _syncSearchFormStickyHeight() {
    var form = document.getElementById('search-form');
    if (!form) return;
    var h = form.getBoundingClientRect().height;
    if (h > 0) {
      document.documentElement.style.setProperty('--search-form-sticky-height', Math.round(h) + 'px');
    }
  }

  /**
   * Переход на элемент цепочки по id (используется из breadcrumb и siblings-menu).
   * Устанавливает CURRENT_HISTORY_ID, PENDING_PARENT/TERM, обновляет UI и запускает поиск.
   */
  function _navigateToChainId(id, opts) {
    opts = opts || {};
    var record = window.getHistoryById ? window.getHistoryById(id) : null;
    if (!record) return;

    window.CURRENT_HISTORY_ID = id;
    if (window.setChainLastActive) window.setChainLastActive(id);
    window.PENDING_PARENT_ID = record.parentId || null;
    window.PENDING_PARENT_TS = Date.now();

    // Синхронизируем breadcrumb и history-кнопку сразу — saveSearchToHistory
    // может сделать early-return при совпадении query, и тогда update не вызовется.
    // skipAutoOpen: навигация через breadcrumb/siblings-menu не должна триггерить
    // авто-открытие боковой панели — пользователь явно работает с breadcrumb UI.
    update(id, { skipAutoOpen: true });

    if (record.termGuid) {
      window.PENDING_TERM_GUID = record.termGuid;
      window.PENDING_TERM_INDEX = null;
    } else if (record.termIndex) {
      window.PENDING_TERM_GUID = null;
      window.PENDING_TERM_INDEX = record.termIndex;
    } else {
      window.PENDING_TERM_GUID = null;
      window.PENDING_TERM_INDEX = null;
    }

    var term = record.query || record.term || '';
    var searchInput = document.getElementById('search');
    if (searchInput) searchInput.value = term;

    if (record.searchType) {
      var hidden = document.getElementById('search-type-hidden');
      if (hidden) {
        hidden.value = record.searchType;
        var aiToggle = document.getElementById('search-mode-toggle');
        if (aiToggle) {
          if (record.searchType === 'smart') aiToggle.classList.add('active');
          else aiToggle.classList.remove('active');
          aiToggle.setAttribute('data-mode', record.searchType);
        }
      }
    }

    if (opts.scrollTop) {
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (err) { window.scrollTo(0, 0); }
    }

    var currentLang = window.SEARCH_LANGUAGE ? window.SEARCH_LANGUAGE.getCurrentLang() : 'en';
    if (record.lang && record.lang !== currentLang && window.SEARCH_LANGUAGE && window.SEARCH_LANGUAGE.switchLanguage) {
      window.SEARCH_LANGUAGE.switchLanguage(record.lang, term, record.searchType);
    } else if (window.SEARCH && window.SEARCH.performSearch) {
      window.SEARCH.performSearch(null);
    }
  }

  // ---- Siblings dropdown menu (открывается по клику на «+N» чип) ----

  function _ensureSiblingsMenu() {
    var menu = document.getElementById('chain-siblings-menu');
    if (menu) return menu;
    menu = document.createElement('div');
    menu.id = 'chain-siblings-menu';
    menu.className = 'chain-siblings-menu';
    menu.hidden = true;
    menu.setAttribute('role', 'menu');
    document.body.appendChild(menu);
    // Навигация по кликам в меню.
    menu.addEventListener('click', function(e) {
      var btn = e.target.closest && e.target.closest('.chain-siblings-menu-item');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var id = btn.getAttribute('data-chain-id');
      _closeSiblingsMenu();
      if (id) _navigateToChainId(id, { scrollTop: false });
    });
    return menu;
  }

  function _openSiblingsMenu(chipBtn) {
    var currentId = window.CURRENT_HISTORY_ID;
    if (!currentId || !window.getHistoryChildren) return;
    var children = window.getHistoryChildren(currentId).slice().sort(function(a, b) {
      return (b.timestamp || 0) - (a.timestamp || 0);
    });
    if (children.length === 0) return;

    var esc = _chainEsc();
    var menu = _ensureSiblingsMenu();
    menu.innerHTML = children.map(function(child) {
      var label = esc(child.displayTerm || child.term || child.query || '');
      return '<button type="button" class="chain-siblings-menu-item" data-chain-id="' + esc(child.id) + '" title="' + label + '" role="menuitem">' + label + '</button>';
    }).join('');
    menu.hidden = false;
    chipBtn.setAttribute('aria-expanded', 'true');
    menu._activeChip = chipBtn;

    // Позиционируем: ниже чипа, правый край меню совмещаем с правым краем чипа.
    // Clamp в viewport; если снизу не помещается — показываем сверху.
    var rect = chipBtn.getBoundingClientRect();
    // reset для корректного измерения
    menu.style.top = '0px';
    menu.style.left = '0px';
    var mw = menu.offsetWidth;
    var mh = menu.offsetHeight;
    var top = rect.bottom + 4;
    var left = rect.right - mw;
    left = Math.max(8, Math.min(left, window.innerWidth - mw - 8));
    if (top + mh > window.innerHeight - 8) {
      top = Math.max(8, rect.top - mh - 4);
    }
    menu.style.top = top + 'px';
    menu.style.left = left + 'px';
  }

  function _closeSiblingsMenu() {
    var menu = document.getElementById('chain-siblings-menu');
    if (!menu || menu.hidden) return;
    menu.hidden = true;
    if (menu._activeChip) {
      menu._activeChip.setAttribute('aria-expanded', 'false');
      menu._activeChip = null;
    }
  }

  function _bindBreadcrumbClicks() {
    var el = document.getElementById('chain-breadcrumb');
    if (!el || el._chainBreadcrumbBound) return;
    el._chainBreadcrumbBound = true;

    el.addEventListener('click', function(e) {
      // Клик по «+N» чипу — открыть/закрыть меню сиблингов.
      var chipBtn = e.target.closest && e.target.closest('.chain-breadcrumb-siblings');
      if (chipBtn) {
        e.preventDefault();
        e.stopPropagation();
        if (chipBtn.getAttribute('aria-expanded') === 'true') {
          _closeSiblingsMenu();
        } else {
          _openSiblingsMenu(chipBtn);
        }
        return;
      }

      var btn = e.target.closest && e.target.closest('.chain-breadcrumb-item');
      if (!btn) return;
      e.preventDefault();
      var id = btn.getAttribute('data-chain-id');
      if (!id) return;
      var isCurrent = btn.classList.contains('is-current');
      _navigateToChainId(id, { scrollTop: isCurrent });
    });

    // Глобальные слушатели закрытия меню.
    if (!document._chainSiblingsGlobalBound) {
      document._chainSiblingsGlobalBound = true;
      document.addEventListener('click', function(e) {
        if (e.target.closest('#chain-siblings-menu')) return;
        if (e.target.closest('.chain-breadcrumb-siblings')) return;
        _closeSiblingsMenu();
      });
      window.addEventListener('scroll', _closeSiblingsMenu, true);
      window.addEventListener('resize', _closeSiblingsMenu);
    }
  }

  // ---- Main update ----

  function update(historyId, opts) {
    opts = opts || {};
    // Navigation to another node → pending-save на старом узле неактуален.
    // Дискардим unsaved changes и убираем индикатор (inline или floating fab).
    if (window._CHAIN_PENDING_SAVE && window._CHAIN_PENDING_SAVE.id !== historyId) {
      if (window.clearPendingSave) window.clearPendingSave();
      _hidePendingSave();
    }

    if (!historyId) {
      _currentChain = [];
      _currentRootId = null;
      _updateFabVisibility();
      _updateBreadcrumb([], null);
      _updateHistoryButton(false, 0);
      if (_isOpen) close();
      return;
    }

    var chain = [];
    if (window.getHistoryRootChain) chain = window.getHistoryRootChain(historyId);
    else if (typeof getRootChain === 'function') chain = getRootChain(historyId);

    _currentChain = chain;
    var newRootId = chain.length > 0 ? chain[0].id : null;
    _currentRootId = newRootId; // set before _branchHasChain() checks it

    // In-chain = есть предки (chain.length >= 2) ИЛИ у current есть прямые потомки в истории.
    var hasDirectChildren = window.getHistoryChildren ? window.getHistoryChildren(historyId).length > 0 : false;
    var inChain = chain.length >= 2 || hasDirectChildren;

    _updateFabVisibility();
    _updateBreadcrumb(chain, historyId);
    _updateHistoryButton(inChain, _countChainTree(newRootId));
    if (_isOpen) _updatePanelContent();

    // skipAutoOpen: вызов из updateActiveHistoryDisplay (обновление displayTerm
    // на текущем leaf) — не должен триггерить авто-открытие панели.
    if (opts.skipAutoOpen) return;

    // Auto-show side panel ТОЛЬКО когда ВПЕРЁД от текущего элемента (т.е.
    // в его поддереве + сам он) есть развилка — узел с >= 2 прямыми потомками.
    // Линейный «хвост» автоматом панель не открывает — достаточно breadcrumb.
    // Если панель уже открыта — не трогаем, она остаётся.
    if (_branchHasChain() && _isDesktop() && !_isOpen && _subtreeHasBranchingFrom(historyId)) {
      _setDismissedForSession(false);
      _setLastAutoRoot(newRootId);
      open();
    } else if (!_branchHasChain() && _isOpen) {
      // Совсем ушли из цепочки (новый поиск без потомков) — закрываем.
      // Линейную цепочку без развилки НЕ закрываем принудительно, чтобы
      // не ломать состояние, если юзер открыл панель вручную.
      close();
    }
  }

  // ---- Pending save indicator ----

  /**
   * Показывает индикатор «есть несохранённые изменения».
   *   - Если боковая панель открыта (desktop) — вставляем save-кнопку внутрь
   *     соответствующего ряда панели (как раньше).
   *   - Иначе (mobile, либо desktop с закрытой панелью) — показываем
   *     плавающую fixed-кнопку #chain-save-fab в правом верхнем углу.
   */
  function showPendingSave(itemId) {
    _pendingSaveId = itemId;
    _wakeFromIdle();
    _clearSaveUI();

    if (_isOpen && _isDesktop()) {
      var body = _body();
      if (!body) return;
      var itemEl = body.querySelector('.search-history-item[data-id="' + itemId + '"]');
      if (!itemEl) return;

      var btn = document.createElement('button');
      btn.className = 'chain-panel-save-btn';
      btn.setAttribute('type', 'button');
      btn.setAttribute('data-save-id', itemId);
      var _t = (window.LANG_TEXTS && window.LANG_TEXTS[
        (window.SEARCH_LANGUAGE && window.SEARCH_LANGUAGE.getCurrentLang) ? window.SEARCH_LANGUAGE.getCurrentLang() : 'en'
      ]) || {};
      btn.setAttribute('aria-label', _t.btn_save || 'Save');
      btn.setAttribute('title', _t.btn_save || 'Save');
      btn.innerHTML = '<i class="bi bi-floppy-fill" aria-hidden="true"></i>';
      itemEl.appendChild(btn);
    } else {
      var fab = document.getElementById('chain-save-fab');
      if (!fab) return;
      fab.setAttribute('data-save-id', itemId);
      fab.hidden = false;
      fab.classList.add('is-visible');
    }
  }

  function _hidePendingSave() {
    _pendingSaveId = null;
    _clearSaveUI();
  }

  /** Снимает визуальный индикатор, не трогая _pendingSaveId. */
  function _clearSaveUI() {
    var body = _body();
    if (body) {
      var existing = body.querySelectorAll('.chain-panel-save-btn');
      existing.forEach(function(b) { b.remove(); });
    }
    var fab = document.getElementById('chain-save-fab');
    if (fab) {
      fab.hidden = true;
      fab.classList.remove('is-visible');
      fab.removeAttribute('data-save-id');
    }
  }

  // ---- Click handler ----

  function _onPanelClick(e) {
    if (e.target.closest('.search-history-toggle')) return;

    // Save button: commit pending changes
    var saveBtn = e.target.closest && e.target.closest('.chain-panel-save-btn');
    if (saveBtn) {
      e.stopPropagation();
      if (window.commitPendingSave) {
        window.commitPendingSave();
        _hidePendingSave();
      }
      return;
    }

    // Delete: double-click confirm pattern (same as dropdown)
    var deleteBtn = e.target.closest && e.target.closest('.search-history-delete');
    if (deleteBtn) {
      e.stopPropagation();
      if (deleteBtn.classList.contains('confirm')) {
        var delId = deleteBtn.getAttribute('data-delete-id');
        if (delId && window.deleteHistoryItem) {
          window.deleteHistoryItem(delId);
          // After deletion, CURRENT_HISTORY_ID may have been redirected
          // to the nearest surviving ancestor. Navigate to it.
          var newActiveId = window.CURRENT_HISTORY_ID;
          if (newActiveId) {
            var rec = window.getHistoryById ? window.getHistoryById(newActiveId) : null;
            if (rec) {
              var searchInput = document.getElementById('search');
              if (searchInput) searchInput.value = rec.query || rec.term || '';
              if (typeof performSearch === 'function') performSearch();
              else if (window.performSearch) window.performSearch();
            }
          }
          update(newActiveId);
        }
      } else {
        // Reset other confirm states, set this one
        var body = _body();
        if (body) {
          body.querySelectorAll('.search-history-delete.confirm').forEach(function(btn) {
            btn.classList.remove('confirm');
          });
        }
        deleteBtn.classList.add('confirm');
      }
      return;
    }

    var itemEl = e.target.closest('.search-history-item');
    if (!itemEl) return;

    var id = itemEl.getAttribute('data-id');
    if (!id) return;

    // Discard unsaved changes when navigating to a different item
    if (window._CHAIN_PENDING_SAVE && window._CHAIN_PENDING_SAVE.id !== id) {
      if (window.clearPendingSave) window.clearPendingSave();
      _hidePendingSave();
    }

    var record = window.getHistoryById ? window.getHistoryById(id) : null;
    if (!record) return;

    var searchInput = document.getElementById('search');
    if (searchInput) searchInput.value = record.query || record.term || '';

    window.CURRENT_HISTORY_ID = id;
    if (window.setChainLastActive) window.setChainLastActive(id);
    // Do NOT call syncHistoryIdToUrl here — it would replaceState on the
    // CURRENT browser history entry before performSearch pushes a new one.
    // saveSearchToHistory will sync hid to URL after the new pushState.

    // Restore pending term index/guid so the correct article auto-expands
    var termGuid = record.termGuid || '';
    var termIndex = record.termIndex || null;
    if (termGuid) {
      window.PENDING_TERM_GUID = termGuid;
      window.PENDING_TERM_INDEX = null;
    } else if (termIndex) {
      window.PENDING_TERM_GUID = null;
      window.PENDING_TERM_INDEX = termIndex;
    } else {
      window.PENDING_TERM_GUID = null;
      window.PENDING_TERM_INDEX = null;
    }

    // Restore search type (AI mode) from when this term was originally searched
    var searchType = record.searchType || '';
    if (searchType) {
      var hiddenInput = document.getElementById('search-type-hidden');
      if (hiddenInput) {
        hiddenInput.value = searchType;
        var aiToggle = document.getElementById('search-mode-toggle');
        if (aiToggle) {
          if (searchType === 'smart') aiToggle.classList.add('active');
          else aiToggle.classList.remove('active');
          aiToggle.setAttribute('data-mode', searchType);
        }
      }
    }

    // Clear PENDING_PARENT — we're navigating to an existing record,
    // NOT creating a new chain link. Stale parent would cause duplicates.
    window.PENDING_PARENT_ID = null;
    window.PENDING_PARENT_TS = 0;

    if (record.lang && window.SEARCH_LANGUAGE && window.SEARCH_LANGUAGE.switchLanguage) {
      var currentLang = window.SEARCH_LANGUAGE.getCurrentLang ? window.SEARCH_LANGUAGE.getCurrentLang() : 'en';
      if (record.lang !== currentLang) {
        window.SEARCH_LANGUAGE.switchLanguage(record.lang, record.query || record.term || '', record.searchType || '');
        _updatePanelContent();
        return;
      }
    }

    if (typeof performSearch === 'function') performSearch();
    else if (window.performSearch) window.performSearch();

    _updatePanelContent();
  }

  // ---- Idle blur ----
  var IDLE_TIMEOUT = 15000; // 15 seconds
  var _idleTimer = null;
  var _isHovered = false;

  function _startIdleTimer() {
    _clearIdleTimer();
    if (_isHovered) return; // don't blur while cursor is over the panel
    _idleTimer = setTimeout(function () {
      var panel = _panel();
      if (panel && _isOpen) panel.classList.add('is-idle');
    }, IDLE_TIMEOUT);
  }

  function _clearIdleTimer() {
    if (_idleTimer) {
      clearTimeout(_idleTimer);
      _idleTimer = null;
    }
  }

  function _wakeFromIdle() {
    var panel = _panel();
    if (panel) panel.classList.remove('is-idle');
    if (_isOpen) _startIdleTimer();
  }

  function _bindIdleEvents() {
    var panel = _panel();
    if (!panel) return;
    panel.addEventListener('mousemove', _wakeFromIdle);
    panel.addEventListener('mouseenter', function () {
      _isHovered = true;
      _wakeFromIdle();
    });
    panel.addEventListener('mouseleave', function () {
      _isHovered = false;
      if (_isOpen) _startIdleTimer();
    });
    panel.addEventListener('click', _wakeFromIdle);
    panel.addEventListener('scroll', _wakeFromIdle, true);
  }

  // ---- Init ----

  function _bindEvents() {
    var body = _body();
    if (body) {
      body.addEventListener('click', _onPanelClick);
      if (window._bindHoverPath) window._bindHoverPath(body);
    }

    var closeBtn = document.getElementById('chain-panel-close');
    if (closeBtn) closeBtn.addEventListener('click', close);

    var fab = _fab();
    if (fab) fab.addEventListener('click', toggle);

    var handle = _resizeHandle();
    if (handle) handle.addEventListener('mousedown', _startResize);

    // Плавающая save-кнопка (mobile + desktop-when-panel-closed).
    var saveFab = document.getElementById('chain-save-fab');
    if (saveFab && !saveFab._chainSaveFabBound) {
      saveFab._chainSaveFabBound = true;
      saveFab.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        if (window.commitPendingSave) window.commitPendingSave();
        _hidePendingSave();
      });
    }

    _bindIdleEvents();
    _bindBreadcrumbClicks();

    // Следим за высотой sticky-формы поиска, чтобы breadcrumb прилипал
    // ровно под ней без перекрытия.
    var searchForm = document.getElementById('search-form');
    if (searchForm && window.ResizeObserver && !searchForm._chainStickyObserverBound) {
      searchForm._chainStickyObserverBound = true;
      var ro = new ResizeObserver(function () { _syncSearchFormStickyHeight(); });
      ro.observe(searchForm);
    }

    window.addEventListener('resize', function () {
      if (!_isDesktop() && _isOpen) close();
      // Re-clamp width on window resize
      _applyWidth(_panelWidth);
      _updateFabVisibility();
      _syncSearchFormStickyHeight();
    });
  }

  function init() {
    // Load saved width from cookie
    _panelWidth = _loadWidthFromCookie();
    _applyWidth(_panelWidth);

    _bindEvents();

    // Сбрасываем pending-save состояние — оно in-memory и после reload
    // не должно существовать (floppy-кнопка должна быть скрыта).
    window._CHAIN_PENDING_SAVE = null;
    _hidePendingSave();

    // Инициализируем CSS-переменную высоты формы, чтобы sticky-breadcrumb
    // правильно позиционировался даже до первого обновления цепочки.
    _syncSearchFormStickyHeight();

    if (window.CURRENT_HISTORY_ID) {
      setTimeout(function () { update(window.CURRENT_HISTORY_ID); }, 100);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 50);
  }

  window.CHAIN_PANEL = {
    open: open,
    close: close,
    toggle: toggle,
    update: update,
    isOpen: isOpen,
    showPendingSave: showPendingSave,
    hidePendingSave: _hidePendingSave
  };
})();
